import { useRef } from 'react'
import { api, unwrap } from '~/shared/lib/rpc'
import type {
  ForwardParams,
  GeocodingResult,
  GeocodingSuggestion,
  RetrieveParams,
  SessionToken,
  SuggestParams,
} from '~/shared/types/geoCoding'

function createSessionToken(): SessionToken {
  return crypto.randomUUID() as SessionToken
}

export function useGeocoding() {
  const sessionRef = useRef<SessionToken | null>(null)
  const debounceRef = useRef<number | null>(null)

  const getToken = (): SessionToken => {
    if (!sessionRef.current) {
      sessionRef.current = createSessionToken()
    }
    return sessionRef.current
  }

  const resetSession = () => {
    sessionRef.current = null
  }

  const suggest = (params: Omit<SuggestParams, 'sessionToken'>): Promise<GeocodingSuggestion[]> => {
    return new Promise((resolve, reject) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }

      debounceRef.current = window.setTimeout(async () => {
        try {
          const url = api.api.geocoding.suggest.$url({
            query: {
              query: params.query,
              sessionToken: getToken(),
              proximity: params.proximity,
              language: params.language,
              country: params.country,
              limit: params.limit === undefined ? undefined : String(params.limit),
            },
          })

          const res = await fetch(url)
          resolve(await unwrap<GeocodingSuggestion[]>(res))
        } catch (err) {
          reject(err)
        }
      }, 350)
    })
  }

  const retrieve = async (
    params: Omit<RetrieveParams, 'sessionToken'>,
  ): Promise<GeocodingResult | null> => {
    const url = api.api.geocoding.retrieve[':mapboxId'].$url({
      param: { mapboxId: params.mapboxId },
      query: { sessionToken: getToken() },
    })

    const res = await fetch(url)
    const data = await unwrap<GeocodingResult>(res)

    resetSession()
    return data
  }

  const forward = async (params: ForwardParams): Promise<GeocodingResult[]> => {
    const url = api.api.geocoding.forward.$url({
      query: {
        query: params.query,
        proximity: params.proximity,
        language: params.language,
        country: params.country,
        limit: params.limit === undefined ? undefined : String(params.limit),
      },
    })

    const res = await fetch(url)
    return unwrap<GeocodingResult[]>(res)
  }

  return {
    suggest,
    retrieve,
    forward,
    resetSession,
  }
}
